import { Component } from '@theme/component';
import { SlideshowSelectEvent } from '@theme/events';

const storageKey = 'party-delights-favourites';

/** @extends {Component<{heart: HTMLButtonElement, dots?: HTMLButtonElement[], status: HTMLElement}>} */
class HenryCardControls extends Component {
  #controller;

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    const options = { signal: this.#controller.signal };
    this.closest('.card-gallery')?.addEventListener(SlideshowSelectEvent.eventName, this.#syncImage, options);
    window.addEventListener('storage', this.#syncFavourite, options);
    document.addEventListener('henry:favourites-changed', this.#syncFavourite, options);
    this.#initialise();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initialise();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
  }

  #initialise() {
    this.refs.heart.hidden = false;
    this.#syncFavourite();
    customElements.whenDefined('slideshow-component').then(() => {
      if (!this.isConnected) return;
      for (const dot of this.refs.dots ?? []) dot.hidden = false;
    });
  }

  #readFavourites() {
    const saved = JSON.parse(localStorage.getItem(storageKey) || '[]');
    return new Set(Array.isArray(saved) ? saved.filter((id) => typeof id === 'string') : []);
  }

  #syncFavourite = () => {
    let saved = false;
    try { saved = this.#readFavourites().has(this.dataset.productId); } catch { /* Remain usable if storage is blocked. */ }
    this.refs.heart.setAttribute('aria-pressed', String(saved));
    this.refs.heart.setAttribute('aria-label', saved ? this.dataset.removeLabel : this.dataset.addLabel);
  };

  /** @param {MouseEvent} event */
  toggleFavourite(event) {
    event.preventDefault();
    event.stopPropagation();
    try {
      const saved = this.#readFavourites();
      const id = this.dataset.productId;
      if (!id) return;
      if (saved.has(id)) saved.delete(id);
      else saved.add(id);
      localStorage.setItem(storageKey, JSON.stringify([...saved]));
      this.refs.status.textContent = '';
      document.dispatchEvent(new CustomEvent('henry:favourites-changed'));
    } catch {
      this.refs.status.textContent = this.dataset.errorLabel;
    }
  }

  /** @param {MouseEvent} event */
  selectImage(event) {
    event.preventDefault();
    event.stopPropagation();
    const dot = event.target instanceof Element ? event.target.closest('[data-media-id]') : null;
    if (!dot) return;
    const slideshow = this.closest('.card-gallery')?.querySelector('slideshow-component');
    slideshow?.select({ id: dot.dataset.mediaId }, event, { animate: !matchMedia('(prefers-reduced-motion: reduce)').matches });
  }

  /** @param {SlideshowSelectEvent} event */
  #syncImage = (event) => {
    for (const dot of this.refs.dots ?? []) {
      dot.setAttribute('aria-current', String(dot.dataset.mediaId === event.detail.id));
    }
  };
}

if (!customElements.get('henry-card-controls')) customElements.define('henry-card-controls', HenryCardControls);
